import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';

import { readTopic, updateTopicVisibility } from '@api/fast_tracks';

import { fastTrackErrorStatuses, fastTrackStatus, flowTopicTag } from '@helpers/fast_tracks';

import { LoaderSkeleton, TopicCard } from '@utilities';

import { getDefaultTopicData } from '../../utilities/helpers';
import TopicCardFooter from '../TopicCardFooter';
import Visualization from '../Visualization';

import SampleSizeFooter from './components/SampleSizeFooter';
import SubscribedFilterFooter from './components/SubscribedFilterFooter';

import styles from './_index.module.scss';

const FastTrackTopicCard = ({
  initialTopicData = null,
  isPreviewActive = false,
  keyIndex,
  onPreview,
  setDownloadData,
  subscribedCategories,
  topic,
}) => {
  const [topicData, setTopicData] = useState(initialTopicData || getDefaultTopicData(topic));
  const topicTags = topicData?.tags || {};
  const includesSubscribedCategories = flowTopicTag.INCLUDES_SUBSCRIBED_CATEGORIES in topicTags;
  const isNoSample = flowTopicTag.NO_SAMPLE in topicTags;
  const isPreviewCard = flowTopicTag.PREVIEW in topicTags;
  const [isExcluded, setIsExcluded] = useState(topic.isHidden || isNoSample || false);

  const exportData = useMemo(() => {
    return {
      reportType: `Narrative`,
      tabName: topicData?.topicId,
    };
  }, [topicData]);
  const sampleSizes = topicData?.visualization?.sample_size || null;

  useEffect(() => {
    if (!initialTopicData) return;
    setTopicData(initialTopicData);
  }, [initialTopicData]);

  useEffect(() => {
    if (topicData?.status !== fastTrackStatus.COMPLETED || !topicData?.id || isPreviewActive)
      return;

    // Initialize downloadData with topic information
    setDownloadData((prevData) => {
      const data = new Map(prevData);
      const topicId = topicData?.id;
      const existingTopic = data.get(topicId);

      data.set(topicId, {
        ...existingTopic,
        id: topicId,
        title: topicData?.visualization?.clean_title || topicData?.title,
      });
      return data;
    });
  }, [topicData, isPreviewActive, setDownloadData]);

  useEffect(() => {
    const controller = new AbortController();

    if (!topicData?.loading) {
      return () => {
        controller.abort();
      };
    }

    const fetchTopic = async () => {
      try {
        const result = await readTopic({ id: topic.id, signal: controller.signal });
        if (result.status === fastTrackStatus.COMPLETED) {
          setTopicData((prevTopicData) => ({
            ...prevTopicData,
            ...result,
            error: false,
            loading: false,
          }));
        } else {
          setTopicData((prevTopicData) => ({
            ...prevTopicData,
            error: true,
            loading: false,
          }));
        }
      } catch (error) {
        console.error(error);
        setTopicData((prevTopicData) => ({
          ...prevTopicData,
          error: true,
          loading: false,
        }));
      }
    };

    if (topic?.status === fastTrackStatus.COMPLETED) {
      fetchTopic();
    } else if (fastTrackErrorStatuses.includes(topic?.status)) {
      setTopicData((prevTopicData) => ({
        ...prevTopicData,
        error: true,
        loading: false,
      }));
    }

    return () => {
      controller.abort();
    };
  }, [topicData?.loading, topic?.status]);

  const handleToggleVisibility = async () => {
    try {
      await updateTopicVisibility({ id: topic.id, isHidden: !isExcluded });
      setIsExcluded((prev) => !prev);
    } catch (error) {
      console.error('Error updating topic visibility:', error);
    }
  };

  if (topicData?.error) {
    return (
      <TopicCard
        footer={
          <span>
            <strong>Excluded </strong>
            This topic is unavailable because of an internal error.
          </span>
        }
        isExcluded={true}
        key={`topic-card-${keyIndex}`}
        title={topic?.title}
      />
    );
  }

  if (topicData?.loading) {
    return (
      <TopicCard
        footer={
          <TopicCardFooter
            handleToggleVisibility={handleToggleVisibility}
            initialFeedback={topic?.feedback}
            isExcluded={isExcluded}
            isLoading={topicData?.loading}
            isPreviewActive={isPreviewActive}
            isPreviewCard={isPreviewCard}
            topicId={topic?.id}
          />
        }
        isExcluded={isExcluded && !isPreviewActive}
        isPreviewCard={isPreviewCard}
        key={`topic-card-${keyIndex}`}
        title={topic?.title}
      >
        <LoaderSkeleton height={210}>
          <rect height="30" rx="15" ry="15" width="65%" x="0" y="30" />
          <rect height="30" rx="15" ry="15" width="65%" x="0" y="90" />
          <rect height="30" rx="15" ry="15" width="65%" x="0" y="150" />
          <rect height="10" rx="7" ry="7" width="20%" x="75%" y="20" />
          <rect height="10" rx="7" ry="7" width="20%" x="75%" y="45" />
          <rect height="10" rx="7" ry="7" width="20%" x="75%" y="70" />
          <rect height="10" rx="7" ry="7" width="10%" x="75%" y="95" />
        </LoaderSkeleton>
        <div className={styles['loading-indicator-text']}>Processing...</div>
      </TopicCard>
    );
  }

  if (
    topicData?.visualization?.datasets?.length === 0 &&
    topicData?.status === fastTrackStatus.COMPLETED
  ) {
    return (
      <TopicCard
        footer={
          <TopicCardFooter
            handleToggleVisibility={handleToggleVisibility}
            initialFeedback={topic?.feedback}
            isExcluded={isExcluded}
            isLoading={topicData?.loading}
            isPreviewCard={isPreviewCard}
            topicId={topic?.id}
          />
        }
        isExcluded={isExcluded}
        key={`topic-card-${keyIndex}`}
        title={topic?.title}
      >
        <small>No topic data available.</small>
      </TopicCard>
    );
  }

  if (isNoSample) {
    return (
      <TopicCard
        footer={
          <span>
            <strong>Excluded </strong>
            {topicData?.tags[flowTopicTag.NO_SAMPLE]}
          </span>
        }
        isExcluded={true}
        key={`topic-card-${keyIndex}`}
        title={topic?.title}
      />
    );
  }

  return (
    <TopicCard
      footer={
        <TopicCardFooter
          handleToggleVisibility={
            isPreviewCard ? () => onPreview(topic?.id, topicData) : handleToggleVisibility
          }
          initialFeedback={topic?.feedback}
          isExcluded={isExcluded && !isPreviewActive}
          isLoading={topicData?.loading}
          isPreviewActive={isPreviewActive}
          isPreviewCard={isPreviewCard}
          topicId={topic?.id}
        />
      }
      isExcluded={isExcluded && !isPreviewActive}
      key={`topic-card-${keyIndex}`}
      title={topic?.title}
    >
      <div className={styles['card-body']}>
        {topicData?.visualization?.datasets?.map((dataset, index) => (
          <div
            className={styles['card-visualization']}
            key={`topic-card-${keyIndex}-dataset-${index}`}
          >
            <div className={styles['card-visual']}>
              <Visualization
                dataset={dataset}
                exportData={exportData}
                isExcluded={isExcluded}
                key={`topic-card-${keyIndex}-visualization-${index}`}
                setDownloadData={setDownloadData}
                topicId={topicData?.id}
              />
            </div>
            <div className={styles['card-takeaways']}>
              <ReactMarkdown children={dataset?.readAs} />
            </div>
          </div>
        ))}
        {sampleSizes && <SampleSizeFooter sampleSizes={sampleSizes} />}
        {includesSubscribedCategories && <SubscribedFilterFooter answers={subscribedCategories} />}
      </div>
    </TopicCard>
  );
};

FastTrackTopicCard.propTypes = {
  initialTopicData: PropTypes.object,
  isPreviewActive: PropTypes.bool,
  keyIndex: PropTypes.number,
  onPreview: PropTypes.func,
  setDownloadData: PropTypes.func,
  subscribedCategories: PropTypes.array,
  topic: PropTypes.object,
};
export default FastTrackTopicCard;
